import React, { useEffect, useState, useRef } from 'react';

export default function SequenceMasterGame({ roomId, isHost, gameState = {}, gamePlayers = [], user = {}, onGameAction, socket }) {
  const [localInput, setLocalInput] = useState([]);
  const localInputRef = useRef(localInput);
  const [transientClicks, setTransientClicks] = useState([]);
  const transientTimersRef = useRef({});
  const [showingIndex, setShowingIndex] = useState(-1);
  const animRef = useRef(null);
  const [activeSequence, setActiveSequence] = useState([]);
  const [readyToShow, setReadyToShow] = useState(false);

  const objectsCount = gameState.objectsCount || 4;
  const sequence = gameState.sequence || [];
  const phase = gameState.phase || 'waiting';
  const colors = ['#ef4444','#f59e0b','#10b981','#1870fdff','#8b5cf6','#ec4899','#1e058aff','#00d9ffff'];
  // detect dark mode (page-level class or prefers-color-scheme) to pick readable borders
  const prefersDark = (typeof window !== 'undefined') && (document.documentElement.classList.contains('dark') || (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches));
  const neutralBorder = prefersDark ? '2px solid rgba(255,255,255,0.12)' : '2px solid rgba(0,0,0,0.08)';

  useEffect(() => {
    // play reveal animation when phase changes to 'showing'
    if (phase === 'showing') {
      // clear any lingering transient click timers from previous rounds
      try {
        if (transientTimersRef.current) {
          Object.values(transientTimersRef.current).forEach(tid => clearTimeout(tid));
        }
      } catch (e) {}
      transientTimersRef.current = {};
      setTransientClicks([]);

      // snapshot sequence so animation is stable even if server-side state mutates
      const seqSnapshot = Array.isArray(sequence) ? sequence.slice() : [];
      setActiveSequence(seqSnapshot);
      setLocalInput([]);
      setShowingIndex(-1);
      setReadyToShow(false);
      let i = 0;
      // store multiple timeout ids so we can clear them all on unmount/cancel
      animRef.current = [];
      // Each step: brief highlight, then at least 1000ms gap where all objects are normal
      const highlightMs = 800; // highlight duration (ms)
      const pauseMs = 1000; // pause between highlights so shape resets (>= 1000ms requested)
      const initialDelay = 1200; // show 'Get Ready' for this long before first highlight

      const playStep = () => {
        // highlight current step index
        setShowingIndex(i);
        // after highlight duration, clear highlight and wait the pause before next
        const t1 = setTimeout(() => {
          // clear highlight so all objects return to identical appearance
          setShowingIndex(-1);
          // after the required pause, advance to next step or finish
          const t2 = setTimeout(() => {
            i += 1;
            if (i < seqSnapshot.length) {
              playStep();
            } else {
              // finished sequence; ensure no highlight remains
              setShowingIndex(-1);
              setActiveSequence([]);
            }
          }, pauseMs);
          animRef.current.push(t2);
        }, highlightMs);
        animRef.current.push(t1);
      };

      // start only if there is something to show
      if (seqSnapshot.length > 0) {
        // initial delay so clients can see 'Get Ready' before sequence
        const t0 = setTimeout(() => {
          setReadyToShow(true);
          playStep();
        }, initialDelay);
        animRef.current.push(t0);
      } else {
        setReadyToShow(true);
      }

      return () => { if (animRef.current && Array.isArray(animRef.current)) { animRef.current.forEach(id => clearTimeout(id)); animRef.current = null; } };
    }
    // ensure highlight cleared when leaving showing phase
    setShowingIndex(-1);
    return undefined;
  }, [phase, sequence.join(',')]);

  const handleStart = () => {
    if (onGameAction && roomId) onGameAction({ type: 'startRound' });
  };

  const autoStartRef = useRef(null);
  useEffect(() => {
    // When waiting for a round, host auto-starts after a short delay.
    // Prevent non-host clients from triggering startRound to avoid duplicate starts.
    if (phase === 'waiting' && !gameState.finished && isHost) {
      // clear any previous timer
      if (autoStartRef.current) clearTimeout(autoStartRef.current);
      // small delay so the player sees 'Get Ready' before the sequence
      autoStartRef.current = setTimeout(() => {
        if (onGameAction && roomId) onGameAction({ type: 'startRound' });
        autoStartRef.current = null;
      }, 1400);
    } else {
      if (autoStartRef.current) { clearTimeout(autoStartRef.current); autoStartRef.current = null; }
    }
    return () => { if (autoStartRef.current) { clearTimeout(autoStartRef.current); autoStartRef.current = null; } };
  }, [phase, roomId, onGameAction, gameState.finished, isHost]);

  // Input phase: remove client-side countdown; rely on server to finalize when all players submitted

  const handleClickObject = (idx) => {
    if (phase !== 'input') return;
    // prevent clicking more than the sequence length
    if (sequence.length && localInput.length >= sequence.length) return;
    // trigger a short visual pulse for feedback, do not persist style changes
    setTransientClicks((prev) => [...prev, idx]);
    // remove transient after animation duration
    try { if (transientTimersRef.current[idx]) clearTimeout(transientTimersRef.current[idx]); } catch (e) {}
    transientTimersRef.current[idx] = setTimeout(() => {
      setTransientClicks((prev) => prev.filter(x => x !== idx));
      try { delete transientTimersRef.current[idx]; } catch (e) {}
    }, 260);

    const next = [...localInput, idx];
    setLocalInput(next);
    localInputRef.current = next;
    // Option: auto-submit when length equals sequence length
    if (sequence.length && next.length >= sequence.length) {
      // submit to server
      if (onGameAction && roomId) onGameAction({ type: 'submitSequence', sequence: next });
    }
  };

  useEffect(() => {
    // keep ref in sync for timer callback
    localInputRef.current = localInput;
  }, [localInput]);

  const renderShape = (i) => {
    const bg = colors[i % colors.length];
    const currentSeq = (phase === 'showing' && Array.isArray(activeSequence) && activeSequence.length > 0) ? activeSequence : sequence;
    const isShowingHighlight = (phase === 'showing' && currentSeq[showingIndex] === i);
    const isTransient = transientClicks.includes(i);
    // Keep base border consistent; only animate transiently
    const baseBorder = neutralBorder;
    const boxShadow = isShowingHighlight ? '0 0 20px rgba(59,130,246,0.9), 0 0 8px rgba(59,130,246,0.6)' : (isTransient ? '0 0 12px rgba(59,130,246,0.6)' : 'none');
    const transform = isShowingHighlight ? 'scale(1.12)' : (isTransient ? 'scale(1.06)' : 'scale(1)');
    return (
      <button key={i} onClick={() => handleClickObject(i)} disabled={phase !== 'input'} style={{
        width: 64, height: 64, margin: 8, background: bg, borderRadius: 12, border: baseBorder, display: 'inline-block', transform, transition: 'transform 160ms ease, box-shadow 160ms ease, border 120ms ease', boxShadow
      }} aria-label={`shape-${i}`} />
    );
  };

  return (
    <div className="p-4">
      { !gameState.finished && (
        <>
      <div className="mb-3">
        <div className="text-sm text-gray-600 dark:text-gray-400">Round {gameState.round || 0} / {gameState.rounds || 0}</div>
      </div>

      <div className="flex items-center flex-wrap" style={{ maxWidth: 600 }}>
        {Array.from({ length: objectsCount }).map((_, i) => renderShape(i))}
      </div>
      </>
        ) }

      {/* Sequence preview removed: objects stay identical until highlighted */}

      {/* <div className="mt-4">
        {isHost && <button className="px-3 py-1 bg-green-600 text-white rounded mr-2" onClick={handleStart}>Start Round</button>}
        {/* Submit button intentionally removed: sequence auto-submits when input length matches sequence length 
      </div> */}

      <div className="mt-3">
          {phase === 'waiting' && <div className="text-center text-2xl font-bold text-indigo-600">Get Ready</div>}
          {phase === 'showing' && !readyToShow && <div className="text-center text-2xl font-bold text-indigo-600">Get Ready</div>}
          {phase === 'showing' && readyToShow && <div className="text-center text-2xl font-bold text-indigo-600">Watch the sequence...</div>}
        {phase === 'input'  && !gameState.finished && (
          <div className="text-sm text-gray-700 dark:text-gray-300">
            <div className="flex items-center justify-between">
              <div>Repeat the sequence by clicking the shapes.</div>
            </div>
            <div className="mt-2 flex items-center">
                      {Array.from({ length: sequence.length || 0 }).map((_, i) => {
                // show a bordered blank box until the player fills it in
                const filled = Array.isArray(localInput) && i < localInput.length;
                const objIndex = filled && localInput[i] != null ? localInput[i] : null;
                const tileColor = objIndex != null ? colors[objIndex % colors.length] : 'transparent';
                const isLast = i === (localInput.length - 1);
                return (
                  <div
                    key={i}
                    style={{
                      width: 20,
                      height: 20,
                      borderRadius: 4,
                      marginRight: 8,
                      background: filled ? tileColor : 'transparent',
                              border: isLast ? '2px solid #2563eb' : neutralBorder
                    }}
                  />
                );
              })}
            </div>
          </div>
        )}
      </div>

      {phase === 'results' && !gameState.finished && (() => {
        const last = Array.isArray(gameState.results) && gameState.results.length ? gameState.results[gameState.results.length-1] : null;
        const seq = last && Array.isArray(last.sequence) ? last.sequence : (gameState.sequence || []);
        const submissions = gameState.submissions || {};
        return (
          <div className="mt-4 text-gray-800 dark:text-gray-200">
            <div className="font-semibold mb-2">Round results</div>
            <div>
                  {gamePlayers.map(p => {
                    const sid = p.socketId;
                    const submitted = Array.isArray(submissions[sid]) ? submissions[sid] : [];
                    // try to read the precomputed per-player result if available
                    const per = last && Array.isArray(last.perPlayer) ? last.perPlayer.find(r => r.socketId === sid) : null;
                    const correctCount = per ? (per.correct || 0) : (() => {
                      let c = 0;
                      for (let i = 0; i < seq.length; i++) {
                        if (typeof submitted[i] !== 'undefined' && submitted[i] === seq[i]) c++;
                      }
                      return c;
                    })();
                    const isPerfect = seq.length > 0 && correctCount === seq.length;
                    return (
                      <div key={sid} className="mb-2 flex items-center">
                        <div style={{ width: 140 }} className="text-sm truncate">{p.username || sid}</div>
                        <div className="ml-3 flex items-center">
                          {Array.from({ length: seq.length || 0 }).map((_, idx) => {
                            const chosen = typeof submitted[idx] !== 'undefined' && submitted[idx] !== null ? submitted[idx] : null;
                            const tileColor = chosen != null ? colors[chosen % colors.length] : 'transparent';
                            const correct = chosen === seq[idx];
                            const symbol = chosen == null ? '' : (correct ? '✓' : '✕');
                            const symbolColor = correct ? '#ffffffff' : '#ffffffff';
                            return (
                              <div key={idx} style={{ width: 24, height: 24, borderRadius: 4, marginRight: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', background: tileColor, border: neutralBorder }}>
                                {symbol ? <span style={{ color: symbolColor, fontWeight: '700', fontSize: 14 }}>{symbol}</span> : null}
                              </div>
                            );
                          })}
                          {isPerfect ? <div className="ml-2 text-sm font-semibold text-green-600">Perfect</div> : null}
                        </div>
                      </div>
                    );
                  })}
            </div>
          </div>
        );
      })()}

      {/* <div className="mt-4 text-sm text-gray-700 dark:text-gray-300">
        <div>Phase: {phase}</div>
        <div>Scoreboard:</div>
        <ul>
          {gamePlayers.map(p => (<li key={p.socketId}>{p.username || p.userId}: { (gameState.scores && gameState.scores[p.socketId]) || 0 }</li>))}
        </ul>
      </div> */}
    </div>
  );
}

SequenceMasterGame.Guide = function SequenceMasterGuide() {
  return (
    <div>
      <h3 className="font-bold mb-2">How to Play Sequence Master</h3>
      <ul className="list-disc ml-6">
        <li>Memorize the sequence of colours shown.</li>
        <li>Repeat the sequence by clicking the colours in the correct order.</li>
        <li>Try to get as many steps of the sequence correct as possible to earn points.</li>
      </ul>
    </div>
  );
};

 
